import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { flowRight } from 'lodash'
import { graphql } from '@apollo/client/react/hoc'
import { withProfiler } from '@sentry/react'
import { withTranslationCustom } from 'lib/i18next'
import { isDealer, isLoggedIn } from 'lib/utils/store/currentUser'
import { withErrorBoundary } from 'lib/errorBoundary'
import { isDev, isProductionHost } from 'lib/env'
import { setInfoBreadcrumb } from 'lib/sentry'
import { openModal } from 'lib/utils/store/modals'
import cookies from 'lib/cookies'
import AppError, { ERROR_TYPE, logException } from 'lib/errors'

import AppHeader from './AppHeader/AppHeader'
import AppFooter from './AppFooter/AppFooter'
import Notifications from './Notifications/Notifications'
import AppErrorHandler from './Errors/AppErrorHandler'
import SavePrompt from './SavePrompt'
import AppLoadingBar from './AppLoadingBar/AppLoadingBar'
import AppVersionHandler from './AppVersionHandler'
import Modals from './Modal/Modals'
import ErrorComponent from './Errors/ErrorComponent'
import AppContext from './AppContext'
import LocationScroll from './Util/LocationScroll/LocationScroll'
import RoutedModals from './Modal/RoutedModals'
import { ExtendedTimeKeeperContainer } from './Forms/ExtendedTimeKeeper/ExtendedTimeKeeper'
import AddToHome from './AddToHome/AddToHome'
import ViewRouter from './ViewRouter'
import OverlayGallery from './Gallery/OverlayGallery/OverlayGallery'
import Loading from './Util/Loading'
import DevFooterTools from './Dev/DevFooterTools/DevFooterTools'
import AuthErrorHandler from './Auth/AuthErrorHandler'
import OnMount from './Util/ReactLifecycle/OnMount'
import ServiceNotification from './Notifications/ServiceNotification/ServiceNotification'
import DealerUserNotification from './Notifications/DelaerUserNotification/DealerUserNotification'
import { ExtendedDateRangePickerContainer } from './Forms/Date/ExtendedDateRangePicker/ExtendedDateRangePicker'
import SentryUpdate from './SentryUpdate'

import getCurrentUser from 'graphql/user/getCurrentUser.gql'

const Layout = ({ t, currentUser, location }) => {
    const [initScreenExited, setInitScreenExited] = useState(false)

    useEffect(() => {
        if (cookies.get('alma-register-tos-modal')) {
            cookies.remove('alma-register-tos-modal')
            openModal('auth-tos-modal')
        }
    }, [])

    const exitInitScreen = () => {
        if (initScreenExited) {
            return
        }

        try {
            const target = document.getElementById('init-screen')
            target.remove()
            setInitScreenExited(true)
        } catch (e) {
            setInfoBreadcrumb('Failed to transition out init screen.')
            logException(e)
        }
    }

    const renderErrorPage = (error) => (
        <div id="app" className="app app--error-page">
            <OnMount onMount={exitInitScreen} />
            {!isDev() && <AppVersionHandler />}

            <div id="page-wrapper" className="page-wrapper">
                <Notifications />
                <AppHeader currentUserLoading={false} />
                <ServiceNotification />
                <main className="app__main">
                    <ErrorComponent error={error} errorPage />
                </main>
            </div>

            <div className="app__secondary">
                <AppFooter />
            </div>

            <Modals />

            <RoutedModals />

            {!isProductionHost() && (
                <DevFooterTools />
            )}
        </div>
    )

    const { loading: currentUserLoading, error: currentUserError } = currentUser

    if (currentUserError) {
        const appError = AppError.readFromApolloError(currentUserError)

        // 401 is expected if user no logged in, so do not render error
        // 403 is expected if checksum is present
        if (!(appError.type === ERROR_TYPE.NETWORK && [401, 403].includes(appError.code))) {
            return renderErrorPage(currentUserError)
        }
    }

    const loggedIn = isLoggedIn(currentUser)

    return (
        <OverlayGallery locationKey={location.key || ''}>
            <OnMount onMount={exitInitScreen} />
            <AddToHome />
            <div id="app" className="app">
                <SentryUpdate currentUser={currentUser} />
                <LocationScroll />
                <SavePrompt />
                {!isDev() && <AppVersionHandler />}

                <div id="page-wrapper" className="page-wrapper">
                    <Notifications />

                    <AppHeader currentUserLoading={currentUserLoading} />

                    <ServiceNotification />

                    {loggedIn && isDealer(currentUser) && <DealerUserNotification />}

                    <AppContext context="main">
                        <AuthErrorHandler t={t} loggedIn={loggedIn} currentUserLoading={currentUserLoading} />
                        <AppErrorHandler>
                            <Loading loading={currentUserLoading}>
                                {() => (
                                    <ViewRouter
                                        loggedIn={loggedIn}
                                        currentUser={currentUser}
                                        currentUserLoading={currentUserLoading}
                                    />
                                )}
                            </Loading>
                        </AppErrorHandler>
                    </AppContext>
                </div>

                <div className="app__secondary">
                    <AppFooter />
                </div>

                <AppLoadingBar />

                <Modals />

                <ExtendedTimeKeeperContainer />
                <ExtendedDateRangePickerContainer />

                {!isProductionHost() && (
                    <DevFooterTools />
                )}
            </div>
        </OverlayGallery>
    )
}

export default flowRight(
    withErrorBoundary,
    withRouter,
    withTranslationCustom('common'),
    graphql(getCurrentUser, { name: 'currentUser' }),
)(withProfiler(Layout, { name: 'Layout' }))
